<template>
  <div class="forum-wrapper">
    <div class="forum-layout">
      <!-- Main Column -->
      <div class="main-column">
        <!-- Admin Need Response Header -->
        <header class="admin-header">
          <h1 class="typo-headline-4">Mensajes que Necesitan Respuesta</h1>
        </header>

        <!-- Tabs for different content types -->
        <div class="admin-tabs">
          <button
            class="tab-button typo-button"
            :class="{ active: activeTab === 'all' }"
            @click.prevent="changeTab('all')"
            :disabled="loading"
          >
            Todos <span class="count">({{ totalCountForTab('all') }})</span>
          </button>
          <button
            class="tab-button typo-button"
            :class="{ active: activeTab === 'topics' }"
            @click.prevent="changeTab('topics')"
            :disabled="loading"
          >
            Hilos <span class="count">({{ totalCountForTab('topics') }})</span>
          </button>
          <button
            class="tab-button typo-button"
            :class="{ active: activeTab === 'comments' }"
            @click.prevent="changeTab('comments')"
            :disabled="loading"
          >
            Comentarios <span class="count">({{ totalCountForTab('comments') }})</span>
          </button>
        </div>

        <!-- Content Area -->
        <div class="admin-content">
          <!-- Loading and Error States -->
          <div v-if="loading" class="loading-message typo-body-2">
            <div class="spinner large"></div>
            Cargando todos los mensajes...
          </div>
          <div v-else-if="error" class="error-message typo-body-2">
            {{ error }}
          </div>
           <!-- Use filteredItems.length for the empty check -->
          <div v-else-if="filteredItems.length === 0" class="empty-message typo-body-2">
            No hay mensajes que necesiten respuesta en esta categoría.
          </div>

          <!-- Items List -->
          <!-- Iterate over the computed 'items' property -->
          <div v-else class="items-list">
            <div
              v-for="item in items"
              :key="item.type + '-' + item.id"
              class="need-response-item"
              :class="{ 'topic-item': item.type === 'topic', 'comment-item': item.type === 'comment' }"
            >
              <div class="item-header">
                <div class="item-type-badge" :class="item.type">
                  {{ item.type === 'topic' ? 'Hilo' : 'Comentario' }}
                </div>
                <div class="item-title">
                  <router-link :to="item.url">{{ item.title }}</router-link>
                </div>
              </div>

              <div class="item-meta">
                <div class="meta-left">
                  <span class="item-author">
                    <i class="material-icons">person</i>
                    {{ item.author.name }}
                  </span>
                  <span class="item-date">
                    <i class="material-icons">access_time</i>
                    {{ formatDate(item.created) }}
                  </span>
                </div>
                <div class="meta-right">
                  <button
                    class="resolve-btn"
                    @click="markAsResolved(item)"
                    :disabled="item.resolving || loading"
                  >
                    <i v-if="!item.resolving" class="material-icons">check_circle</i>
                    <span v-else class="spinner small"></span>
                    {{ item.resolving ? 'Resolviendo...' : 'Marcar como resuelto' }}
                  </button>
                </div>
              </div>

              <div v-if="item.type === 'comment' && item.topic_title" class="parent-thread">
                <span class="parent-label">En el hilo:</span>
                <router-link :to="{ name: 'ForumThread', params: { nid: item.nid } }">
                  {{ item.topic_title }}
                </router-link>
              </div>

              <div v-if="item.preview" class="item-preview">
                <!-- Use text preview directly -->
                <div v-text="truncateText(item.preview, 200)"></div>
                <div class="preview-fade"></div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <!-- Use computed 'totalPages' -->
          <div v-if="!loading && totalPages > 1" class="pagination-controls">
            <button
              class="pagination-btn"
              :disabled="currentPage <= 1 || loading"
              @click="goToPage(currentPage - 1, false)"
            >
              <i class="material-icons">chevron_left</i>
              Anterior
            </button>

            <div class="page-info">
              Página {{ currentPage }} de {{ totalPages }}
            </div>

            <button
              class="pagination-btn"
              :disabled="currentPage >= totalPages || loading"
              @click="goToPage(currentPage + 1, false)"
            >
              Siguiente
              <i class="material-icons">chevron_right</i>
            </button>
          </div>
        </div>
      </div>

      <!-- Side Column -->
      <div class="side-column">
        <user-activity-widget />
        <navigation-links />
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/features/forum/services/forumApi';
import UserActivityWidget from '../components/UserActivityWidget.vue';
import NavigationLinks from '../components/NavigationLinks.vue';

const axios = forumApi;

export default {
  name: 'AdminNeedResponse',
  components: {
    UserActivityWidget,
    NavigationLinks
  },
  data() {
    return {
      loading: true,
      error: null,
      activeTab: 'all',
      currentPage: 1,
      limit: 10, // Items per page
      allItems: [], // <-- Store ALL fetched items here
       // Track resolving state globally to disable actions during POST
      isResolving: false,
      // Store individual item resolving state temporarily
      resolvingItemId: null,
      routeUpdateTimer: null,
      initialLoadComplete: false
    };
  },
  computed: {
    /**
     * Filters allItems based on the activeTab.
     */
    filteredItems() {
      if (this.activeTab === 'all') {
        return this.allItems;
      }
      return this.allItems.filter(item => item.type === this.activeTab.slice(0, -1)); // 'topics' -> 'topic', 'comments' -> 'comment'
    },

    /**
     * Calculates the total number of pages for the currently filtered items.
     */
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.limit));
    },

    /**
     * Calculates the slice of filteredItems to display on the current page.
     */
    items() {
      const start = (this.currentPage - 1) * this.limit;
      const end = start + this.limit;
      return this.filteredItems.slice(start, end);
    }
  },
  watch: {
    // Watcher for route query changes (tab, page)
    '$route.query': {
      handler(newQuery) {
        // Only use this on initial component load
        if (!this.initialLoadComplete) {
          const newTab = newQuery.tab || 'all';
          const newPage = parseInt(newQuery.page) || 1;

          this.activeTab = newTab;
          this.currentPage = Math.max(1, Math.min(newPage, this.totalPages));
          this.initialLoadComplete = true;
        }
      },
      immediate: true
    },

    // Watcher for totalPages changes to adjust currentPage if it becomes invalid
    totalPages(newTotalPages) {
      if (this.currentPage > newTotalPages) {
         this.goToPage(newTotalPages, false); // Go to the last available page without updating URL
      }
    },

     // Watcher for activeTab changes to reset page to 1
     activeTab() {
        // Only reset if current page is not already 1, to avoid redundant navigation
        if (this.currentPage !== 1) {
            this.goToPage(1, true); // Reset to page 1 when tab changes, update URL
        } else {
            // Even if we're already on page 1, we still need to update the URL for the tab change
            this.updateRouteQuery();
        }
     }
  },
  created() {
    // Add a flag to track initial load
    this.initialLoadComplete = false;
    
    // Add a popstate event listener to handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState);
    
    // Fetch data when the component is created
    this.fetchItems();
  },
  beforeDestroy() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener('popstate', this.handlePopState);
  },
  methods: {
    /**
     * Fetches ALL items needing response from the backend ONCE.
     */
    fetchItems() {
      this.loading = true;
      this.error = null;
      this.allItems = []; // Clear previous data

      // Make API call - remove type, page, limit params assuming backend returns all
      axios.get('/forum/api/need-response/get')
        .then(response => {
          if (response.data && response.data.success && response.data.results) {
            const apiResults = response.data.results;

            // Process topics
            const topics = (apiResults.topics || []).map(topic => ({
              ...topic,
              type: 'topic',
              id: topic.nid,
              // Ensure 'preview' exists, even if empty or needs extraction
              preview: topic.body?.summary || topic.body?.value || '',
              // Add resolving state for the button
              resolving: false,
              // Consistent URL structure (use Vue Router if possible)
              url: { name: 'ForumThread', params: { nid: topic.nid } }
            }));

            // Process comments
            const comments = (apiResults.comments || []).map(comment => ({
              ...comment,
              type: 'comment',
              id: comment.cid,
              // Ensure 'preview' exists, e.g., from comment body if available
              // NOTE: Backend PHP doesn't fetch comment body, so preview might be empty
              preview: comment.comment_body?.value || '',
              // Add resolving state
              resolving: false,
              // Ensure title exists
              title: comment.subject || `RE: ${comment.topic_title || 'Hilo desconocido'}`,
              // Consistent URL structure
              url: { name: 'ForumThread', params: { nid: comment.nid }, hash: `#comment-${comment.cid}` }
            }));

            // Combine and store ALL items
            this.allItems = [...topics, ...comments];

            // Optional: Sort allItems initially if desired (e.g., by creation date descending)
            this.allItems.sort((a, b) => (b.created || 0) - (a.created || 0));

             // Initial adjustment of currentPage based on loaded data
             this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));

          } else {
            throw new Error(response.data?.message || 'La respuesta del servidor no es válida.');
          }
        })
        .catch(error => {
          console.error('Error fetching all need response items:', error);
          this.error = `Error al cargar mensajes: ${error.message}. Intente de nuevo más tarde.`;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Calculates the count for a specific tab based on allItems.
     */
    totalCountForTab(tab) {
      if (this.loading) return '...'; // Show loading indicator during initial fetch

      if (tab === 'all') {
        return this.allItems.length;
      } else if (tab === 'topics') {
        // More efficient than filtering every time if counts are needed often
        return this.allItems.reduce((count, item) => count + (item.type === 'topic' ? 1 : 0), 0);
      } else if (tab === 'comments') {
        return this.allItems.reduce((count, item) => count + (item.type === 'comment' ? 1 : 0), 0);
      }
      return 0;
    },

     updateRouteQuery() {
        // Use history.replaceState instead of router.replace to avoid triggering navigation events
        clearTimeout(this.routeUpdateTimer);
        this.routeUpdateTimer = setTimeout(() => {
            const newQuery = {};
            if (this.activeTab !== 'all') newQuery.tab = this.activeTab;
            
            // Build the new URL with query parameters
            const baseUrl = window.location.pathname;
            const queryString = Object.keys(newQuery).length > 0 
                ? '?' + Object.keys(newQuery)
                    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(newQuery[key])}`)
                    .join('&')
                : '';
            
            // Use history.replaceState to update the URL without causing a navigation
            const newUrl = baseUrl + queryString;
            window.history.replaceState(
                { ...history.state, tab: this.activeTab, page: this.currentPage },
                document.title,
                newUrl
            );
        }, 100);
    },

    changeTab(tab) {
        if (tab !== this.activeTab && !this.loading) {
            // Update the local activeTab state directly
            this.activeTab = tab;
            // Update URL without causing navigation
            this.updateRouteQuery();
        }
    },

    goToPage(page, updateUrl = false) {
        const targetPage = Math.max(1, Math.min(page, this.totalPages));
        if (targetPage !== this.currentPage && !this.loading) {
            this.currentPage = targetPage;
            // Only update route if explicitly requested (e.g., for tab changes)
            if (updateUrl) {
                this.updateRouteQuery();
            }
        }
    },

    /**
     * Marks an item as resolved by sending a request to the backend
     * and removing the item from the local allItems array on success.
     */
    markAsResolved(item) {
      if (this.isResolving) return; // Prevent multiple requests

      this.isResolving = true;
      this.resolvingItemId = item.type + '-' + item.id; // Track which item is resolving

      // Find the item in the main array to potentially update its state directly
      const itemInAllItems = this.allItems.find(i => i.type === item.type && i.id === item.id);
      if (itemInAllItems) {
         itemInAllItems.resolving = true; // Show resolving state on the specific item
      }


      const requestData = {
        value: false,
        post_type: item.type,
        topic_id: item.nid
      };
      if (item.type === 'comment') {
        requestData.comment_id = item.cid;
      }

      axios.post('/forum/api/need-response/set', requestData)
        .then(response => {
          if (response.data && response.data.success) {
            // SUCCESS: Remove item from allItems
            this.allItems = this.allItems.filter(i => !(i.type === item.type && i.id === item.id));

            // No need to manually adjust page numbers, watchers and computed props handle it.
            // If current page becomes empty, watcher on totalPages will trigger adjustment.

          } else {
            throw new Error(response.data?.message || 'Error al marcar como resuelto en el servidor.');
          }
        })
        .catch(error => {
          console.error('Error marking as resolved:', error);
          this.error = `Error al marcar como resuelto: ${error.message || 'Error desconocido'}`;
          // Reset resolving state on error
          if (itemInAllItems) {
             itemInAllItems.resolving = false;
          }
        })
        .finally(() => {
          this.isResolving = false;
          this.resolvingItemId = null;
           // Ensure resolving state is false even if item was removed during process
           // Not strictly necessary now as item is removed, but good practice
           item.resolving = false;
        });
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp * 1000);
        if (isNaN(date.getTime())) {
          return 'Fecha inválida';
        }
        return date.toLocaleDateString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        });
      } catch (e) {
        return 'Fecha inválida';
      }
    },

    /**
     * Truncates plain text. Assumes item.preview is text or can be treated as such.
     */
    truncateText(text, maxLength) {
        if (!text) return '';
        if (text.length <= maxLength) {
            return text;
        }
        return text.substring(0, maxLength) + '...';
    },

    /**
     * Handle browser back/forward navigation
     */
    handlePopState(event) {
      // Parse the URL to get the current state
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get('tab') || 'all';
      
      // Update the component state without triggering a reload
      this.activeTab = tab;
      // Reset page to 1 when tab changes through browser navigation
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      }
    }
  }
};
</script>

<style scoped>
/* Styles remain largely the same as the previous version */
/* Basic Spinner Styles */
@keyframes spin {
  to { transform: rotate(360deg); }
}
.spinner {
  display: inline-block;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-highlight); /* Or primary color */
  animation: spin 1s linear infinite;
}
.spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
}
.spinner.large {
  width: 32px;
  height: 32px;
  border-width: 3px;
  margin-right: 10px;
  vertical-align: middle;
}

/* Add vertical alignment to loading message */
.loading-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: var(--color-text-primary);
}


.forum-wrapper {
  background-color: var(--color-background);
  min-height: 100vh;
  padding: 40px 20px;
}

.forum-layout {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 20px;
}

.main-column {
  flex: 3;
  color: var(--color-text-primary);
}

.side-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.admin-header {
  margin-bottom: 20px;
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 15px 15px 0 0;
  text-align: center;
}

.admin-header h1 {
  margin: 0;
}

.admin-tabs {
  display: flex;
  background-color: var(--color-surface);
  padding: 0 20px;
  border-bottom: 1px solid var(--color-outline);
}

.tab-button {
  background: none;
  border: none;
  padding: 15px;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 1rem;
  position: relative;
  transition: color 0.3s ease;
}
.tab-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.tab-button:hover:not(:disabled) {
  color: var(--color-highlight);
}

.tab-button.active {
  color: var(--color-highlight);
  font-weight: 500;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: var(--color-highlight);
}

.count {
  font-size: 0.85rem;
  opacity: 0.8;
  margin-left: 4px;
}

.admin-content {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 0 0 15px 15px;
  min-height: 300px; /* Ensure content area has some height */
  position: relative; /* Needed for absolute positioning of overlays if any */
}


.error-message {
  padding: 40px 20px;
  text-align: center;
  color: #f44336; /* Standard error color */
  font-weight: 500;
  background-color: rgba(244, 67, 54, 0.1); /* Light red background */
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 8px;
}

.empty-message {
  padding: 40px 20px;
  text-align: center;
  color: var(--color-text-primary);
  font-style: italic;
}

.items-list {
   margin-top: 10px;
}

.need-response-item {
  background-color: var(--color-background);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid transparent;
  transition: background-color 0.2s ease, opacity 0.3s ease; /* Added opacity transition */
}
.need-response-item:hover {
   background-color: var(--color-active-surface);
}

/* Style for item being resolved */
.need-response-item[resolving="true"] {
    opacity: 0.6;
    pointer-events: none; /* Prevent interaction while resolving */
}


.topic-item {
  border-left-color: #2196f3; /* Blue for topics */
}

.comment-item {
  border-left-color: #4caf50; /* Green for comments */
}

.item-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

.item-type-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  flex-shrink: 0; /* Prevent shrinking */
}

.item-type-badge.topic {
  background-color: #2196f3;
}

.item-type-badge.comment {
  background-color: #4caf50;
}

.item-title {
  font-size: 1.1rem;
  font-weight: 500;
  flex-grow: 1;
}

.item-title a {
  color: var(--color-text-primary);
  text-decoration: none;
  transition: color 0.3s ease;
}

.item-title a:hover {
  color: var(--color-highlight);
  text-decoration: underline;
}

.item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 15px;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.meta-left {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
}

.item-author,
.item-date {
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.item-author i,
.item-date i {
  font-size: 16px;
  margin-right: 5px;
  vertical-align: middle;
}

.meta-right {
   flex-shrink: 0;
}

.resolve-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s ease, opacity 0.3s ease;
  white-space: nowrap;
  min-height: 30px;
}

.resolve-btn:hover:not(:disabled) {
  background-color: #388e3c;
}

.resolve-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed; /* Use wait only if isResolving */
  background-color: #69a16c;
}
/* Specific cursor when globally resolving */
body[is-resolving="true"] .resolve-btn {
    cursor: wait;
}


.resolve-btn i {
  font-size: 16px;
  display: block;
}

.resolve-btn .spinner.small {
    margin: 0 2px;
}


.parent-thread {
  margin-top: 8px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  padding: 6px 10px;
  background-color: var(--color-active-surface);
  border-radius: 4px;
  border-left: 3px solid var(--color-outline);
}

.parent-label {
  color: var(--color-text-secondary);
  margin-right: 5px;
  font-weight: 500;
}

.parent-thread a {
  color: var(--color-highlight);
  text-decoration: none;
}

.parent-thread a:hover {
  text-decoration: underline;
}

.item-preview {
  background-color: var(--color-background);
  border: 1px solid var(--color-outline);
  padding: 10px 15px;
  border-radius: 4px;
  position: relative;
  max-height: 80px;
  overflow: hidden;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-top: 10px;
  /* Ensure long words break */
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.item-preview > div { /* Target the div holding the text */
    max-height: calc(80px - 20px); /* Adjust based on padding */
    overflow: hidden;
}

.preview-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(to bottom, rgba(var(--color-background-rgb, 255, 255, 255), 0), var(--color-background));
  pointer-events: none;
}


.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--color-outline);
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-outline);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
  font-size: 0.9rem;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-active-surface);
  border-color: var(--color-highlight);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: var(--color-outline);
  background-color: var(--color-surface);
}
.pagination-btn i {
   font-size: 20px;
}

.page-info {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .forum-layout { flex-direction: column; }
  .main-column, .side-column { width: 100%; flex: unset; }
  .side-column { margin-top: 20px; }
  .admin-tabs {
     padding: 0 5px; overflow-x: auto; white-space: nowrap;
     scrollbar-width: thin; scrollbar-color: var(--color-outline) var(--color-surface);
  }
  .admin-tabs::-webkit-scrollbar { height: 5px; }
  .admin-tabs::-webkit-scrollbar-track { background: var(--color-surface); }
  .admin-tabs::-webkit-scrollbar-thumb { background-color: var(--color-outline); border-radius: 10px; border: 1px solid var(--color-surface); }
  .tab-button { padding: 12px 10px; font-size: 0.9rem; }
  .item-meta { flex-direction: column; align-items: flex-start; gap: 8px; }
  .meta-right { width: 100%; }
  .resolve-btn { width: 100%; justify-content: center; padding: 10px 12px; }
  .pagination-controls { flex-direction: column; gap: 15px; }
  .pagination-btn { width: 100%; justify-content: center; }
  .page-info { order: -1; }
}

/* Ensure background gradient uses RGB variable if available */
:root { /* Define --color-background-rgb if not standard */ }
.preview-fade {
  background: linear-gradient(to bottom, rgba(var(--color-background-rgb, 255, 255, 255), 0), var(--color-background));
}
</style>